// src/pages/AdminPage.jsx
//
// ═══════════════════════════════════════════════════════════════════════
// READ-ONLY TRANSITION (July 2026) — ADMIN DECOUPLING
//
// All admin WRITE actions have moved to Marketpulze-ops
// (operations.marketpulze.com). This page is deliberately NOT deleted —
// it remains a read-only mirror of Users/Requests/Testers, kept so the
// owner can spot-check that what ops shows matches what the screener
// sees, during the transition period before this panel is fully retired.
//
// Config, Settings, System Status, Audit Log, and Subscriptions have been
// REMOVED entirely (not just made read-only) — Marketpulze-ops already
// has full parity on the first four, and Subscriptions was always a
// Phase 2 placeholder with nothing behind it. There was no cross-check
// value identified in duplicating those here, unlike Users/Requests/
// Testers.
//
// Every action button (Note, Suspend, Restore, Approve, Reject, Add/
// Reset/Delete tester, Beta toggle, Admin management) has been removed
// from this page. The backend routes those buttons used to call
// (backend/routes/admin.js) still exist but now return 410 Gone if
// called directly — see that file's own header comment for the full
// reasoning. Removing the buttons here is the primary defense (nobody
// can click something that isn't there); the 410 on the backend is
// defense-in-depth for anything that might still call those URLs
// directly (a stale cached build, a bookmark, muscle memory).
// ═══════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { Shield, Mail, Clock, AlertCircle, Users, FlaskConical, Crown, UserCog, ExternalLink } from 'lucide-react';
import { API_URL } from '../styles/shared';

const OPS_PORTAL_URL = 'https://operations.marketpulze.com';

const statusBadge = (status) => {
  const map = { pending:'#f59e0b', approved:'#00ff88', rejected:'#ef4444', active:'#00ff88', suspended:'#ef4444' };
  const color = map[status] || '#64748b';
  return <span style={{ padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:700, background:color+'22', color, border:'1px solid '+color+'44' }}>{status.toUpperCase()}</span>;
};
const testBadge  = () => <span style={{ padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:700, background:'rgba(0,212,255,0.15)', color:'#00d4ff', border:'1px solid rgba(0,212,255,0.3)' }}>TEST</span>;
const superBadge = () => <span style={{ padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:700, background:'rgba(255,215,0,0.15)', color:'#ffd700', border:'1px solid rgba(255,215,0,0.3)' }}>SUPER</span>;
const adminBadge = () => <span style={{ padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:700, background:'rgba(0,212,255,0.15)', color:'#00d4ff', border:'1px solid rgba(0,212,255,0.3)' }}>ADMIN</span>;

const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : 'Never';
const fmtFull = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : 'Never';
const authH   = (token) => ({ Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' });

export default function AdminPage({ user, token }) {
  const [role, setRole]         = useState(null);
  const [tab, setTab]           = useState('users');
  const [members, setMembers]   = useState([]);
  const [requests, setRequests] = useState([]);
  const [testers, setTesters]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const flash = (msg) => { setError(msg); setTimeout(() => setError(''), 5000); };

  const get = useCallback(async (url) => { const r = await fetch(API_URL + url, { headers: authH(token) }); return r.json(); }, [token]);

  const loadRole     = useCallback(async () => { const d = await get('/api/admin/my-role'); if (d.success) setRole(d.data); }, [get]);
  const loadMembers  = useCallback(async () => { setLoading(true); const d = await get('/api/admin/members'); if (d.success) setMembers(d.data); else flash(d.message); setLoading(false); }, [get]);
  const loadRequests = useCallback(async () => { setLoading(true); const d = await get('/api/admin/invite-requests'); if (d.success) setRequests(d.data); else flash(d.message); setLoading(false); }, [get]);
  const loadTesters  = useCallback(async () => { setLoading(true); const d = await get('/api/admin/testers'); if (d.success) setTesters(d.data); else flash(d.message); setLoading(false); }, [get]);

  useEffect(() => { loadRole(); }, [loadRole]);

  useEffect(() => {
    if (!role) return;
    if (tab === 'users')    loadMembers();
    else if (tab === 'requests') loadRequests();
    else if (tab === 'testers')  loadTesters();
  }, [tab, role, loadMembers, loadRequests, loadTesters]);

  if (!role) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh', color:'#64748b' }}>Loading...</div>;
  if (!role.isAdmin) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}>
      <div style={{ textAlign:'center', color:'#ef4444' }}><AlertCircle size={40}/><p style={{ marginTop:'1rem' }}>Access denied</p></div>
    </div>
  );

  const TABS = [
    { id:'users',    label:'Users',    icon:Users },
    { id:'requests', label:'Requests', icon:Mail },
    { id:'testers',  label:'Testers',  icon:FlaskConical },
  ];

  const pending = requests.filter(r => r.status === 'pending');

  const Card = ({ children, style }) => (
    <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', padding:'1rem 1.25rem', marginBottom:'0.75rem', ...style }}>{children}</div>
  );

  return (
    <div style={{ padding:'2rem 1rem' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <Shield size={28} color="#00ff88"/>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
              <h1 style={{ margin:0, color:'#f1f5f9', fontSize:'1.4rem', fontWeight:700 }}>Admin Panel</h1>
              {role.isSuperAdmin ? superBadge() : adminBadge()}
            </div>
            <p style={{ margin:0, color:'#64748b', fontSize:'0.8rem' }}>MarketPulze · {user && user.email}</p>
          </div>
        </div>
        <button onClick={() => {
          if (tab==='users') loadMembers();
          else if (tab==='requests') loadRequests();
          else loadTesters();
        }}
          style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.5rem 1rem', background:'rgba(0,212,255,0.08)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:'6px', color:'#00d4ff', cursor:'pointer', fontSize:'0.8rem' }}>
          Refresh
        </button>
      </div>

      {/* Read-only transition banner */}
      <div style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'8px', padding:'0.85rem 1rem', marginBottom:'1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
        <span style={{ color:'#f59e0b', fontSize:'0.82rem', fontWeight:600 }}>
          📖 Read-only — all admin actions (suspend, restore, approve, reject, testers) now happen in the Operations Portal.
        </span>
        <a href={OPS_PORTAL_URL} target="_blank" rel="noopener noreferrer"
          style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.4rem 0.9rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'6px', color:'#f59e0b', fontSize:'0.78rem', fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>
          Open Operations Portal <ExternalLink size={12}/>
        </a>
      </div>

      {/* Role info bar */}
      <div style={{ background: role.isSuperAdmin ? 'rgba(255,215,0,0.05)' : 'rgba(0,212,255,0.05)', border:'1px solid '+(role.isSuperAdmin ? 'rgba(255,215,0,0.15)' : 'rgba(0,212,255,0.15)'), borderRadius:'8px', padding:'0.6rem 1rem', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
        {role.isSuperAdmin ? <Crown size={14} color="#ffd700"/> : <UserCog size={14} color="#00d4ff"/>}
        <span style={{ color: role.isSuperAdmin ? '#ffd700' : '#00d4ff', fontSize:'0.8rem', fontWeight:600 }}>
          {role.isSuperAdmin ? 'Super Admin — read-only view' : 'Admin — read-only view'}
        </span>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'0.25rem', marginBottom:'1.5rem', borderBottom:'1px solid rgba(255,255,255,0.08)', paddingBottom:'0.5rem' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.6rem 1.1rem', borderRadius:'8px', border:'none', background: tab===t.id ? 'rgba(0,255,136,0.12)' : 'transparent', color: tab===t.id ? '#00ff88' : '#64748b', cursor:'pointer', fontSize:'0.85rem', fontWeight: tab===t.id ? 700 : 500 }}>
            <t.icon size={14}/>
            {t.label}
            {t.id==='requests' && pending.length > 0 && <span style={{ background:'#f59e0b', color:'#000', borderRadius:'10px', padding:'1px 6px', fontSize:'10px', fontWeight:700 }}>{pending.length}</span>}
          </button>
        ))}
      </div>

      {/* Alerts */}
      {error && <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid #ef4444', borderRadius:'8px', padding:'0.75rem 1rem', color:'#ef4444', marginBottom:'1rem', fontSize:'0.85rem' }}>{error}</div>}
      {loading && <div style={{ textAlign:'center', padding:'3rem', color:'#64748b' }}>Loading...</div>}

      {/* ── USERS (read-only) ── */}
      {!loading && tab === 'users' && (
        <div>
          <p style={{ color:'#64748b', fontSize:'0.85rem', marginBottom:'1rem' }}>{members.length} registered member{members.length!==1?'s':''} · excludes testers</p>
          {members.length === 0
            ? <div style={{ textAlign:'center', padding:'3rem', color:'#64748b' }}><Users size={40} style={{ opacity:0.3 }}/><p style={{ marginTop:'1rem' }}>No members registered yet</p></div>
            : members.map(m => (
              <Card key={m._id}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                  <div style={{ flex:1, minWidth:'200px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
                      <span style={{ color:'#f1f5f9', fontWeight:600 }}>{m.email}</span>
                      {statusBadge(m.status || 'active')}
                      {m.isTestAccount && testBadge()}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'0.4rem', marginTop:'0.6rem' }}>
                      {[['Joined',fmt(m.createdAt)],['Last login',fmt(m.lastLogin)],['Analyses/mo',m.analysesThisMonth||0],['Tier',m.subscriptionTier||'free']].map(([k,v]) => (
                        <span key={k} style={{ color:'#64748b', fontSize:'0.75rem' }}>{k}: <span style={{ color:'#94a3b8' }}>{v}</span></span>
                      ))}
                    </div>
                    {m.adminNotes && <p style={{ margin:'0.4rem 0 0', color:'#f59e0b', fontSize:'0.78rem' }}>📝 {m.adminNotes}</p>}
                  </div>
                </div>
              </Card>
            ))
          }
        </div>
      )}

      {/* ── REQUESTS (read-only) ── */}
      {!loading && tab === 'requests' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem', marginBottom:'1.5rem' }}>
            {[['Pending',requests.filter(r=>r.status==='pending').length,'#f59e0b'],['Approved',requests.filter(r=>r.status==='approved').length,'#00ff88'],['Rejected',requests.filter(r=>r.status==='rejected').length,'#ef4444']].map(([label,count,color]) => (
              <div key={label} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid '+color+'33', borderRadius:'10px', padding:'1.25rem', textAlign:'center' }}>
                <div style={{ fontSize:'2rem', fontWeight:700, color }}>{count}</div>
                <div style={{ fontSize:'0.8rem', color:'#64748b', marginTop:'0.25rem' }}>{label}</div>
              </div>
            ))}
          </div>
          {requests.length === 0
            ? <div style={{ textAlign:'center', padding:'3rem', color:'#64748b' }}><Mail size={40} style={{ opacity:0.3 }}/><p style={{ marginTop:'1rem' }}>No invite requests yet</p></div>
            : requests.map(r => (
              <Card key={r._id}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
                      <span style={{ color:'#f1f5f9', fontWeight:600 }}>{r.email}</span>
                      {statusBadge(r.status)}
                    </div>
                    {r.reason && <p style={{ margin:'0.3rem 0 0', color:'#94a3b8', fontSize:'0.8rem' }}>"{r.reason}"</p>}
                    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginTop:'0.3rem', color:'#475569', fontSize:'0.75rem' }}>
                      <Clock size={11}/> {fmtFull(r.submittedAt)}
                      {r.actedBy && <span>· by {r.actedBy}</span>}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          }
        </div>
      )}

      {/* ── TESTERS (read-only) ── */}
      {!loading && tab === 'testers' && (
        <div>
          {testers.length === 0
            ? <div style={{ textAlign:'center', padding:'3rem', color:'#64748b' }}><FlaskConical size={40} style={{ opacity:0.3 }}/><p style={{ marginTop:'1rem' }}>No testers yet</p></div>
            : testers.map(t => (
              <Card key={t._id}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      <span style={{ color:'#f1f5f9', fontWeight:600 }}>{t.email}</span>
                      {testBadge()}{statusBadge(t.status || 'active')}
                    </div>
                    <div style={{ display:'flex', gap:'1rem', marginTop:'0.4rem', flexWrap:'wrap' }}>
                      <span style={{ color:'#64748b', fontSize:'0.75rem' }}>Last login: {fmt(t.lastLogin)}</span>
                      <span style={{ color:'#64748b', fontSize:'0.75rem' }}>Analyses: {t.analysesThisMonth||0} / unlimited</span>
                      <span style={{ color:'#64748b', fontSize:'0.75rem' }}>Joined: {fmt(t.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          }
        </div>
      )}

    </div>
  );
}
